import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ViewEncapsulation,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  Gridster,
  GridsterConfig,
  GridType,
  CompactType,
  DisplayGrid,
} from 'angular-gridster2';
import { FormsModule } from '@angular/forms';
import { ComponentRegistryService } from '../../services/component-registry/component-registry.service';
import { TemplateService } from '../../services/template/template.service';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import {
  MatCheckboxModule,
  MatCheckboxChange,
} from '@angular/material/checkbox';
import { CustomComponent } from '../../models/CustomComponent.model';
import { CustomTemplate } from '../../models/CustomTemplate.model';
import { CustomSlug } from '../../models/CustomSlug.model';
import { TemplateGeneratorDynamicComponent } from '../template-generator-dynamic/template-generator-dynamic.component';
import { TemplateGeneratorStaticComponent } from '../template-generator-static/template-generator-static.component';
import { Subject, debounceTime, filter } from 'rxjs';
import { EditComponentModalComponent } from '../modals/edit-component-modal/edit-component-modal.component';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { PortalModule } from '@angular/cdk/portal';
import { OverlayModule } from '@angular/cdk/overlay';
import { TemplateGeneratorModalComponent } from '../template-generator-modal/template-generator-modal.component';
import { CustomModal } from '../../models/CustomModal.model';
import { Router } from '@angular/router';

@Component({
  selector: 'app-template-generator',
  standalone: true,
  imports: [
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatCheckboxModule,
    TemplateGeneratorDynamicComponent,
    TemplateGeneratorStaticComponent,
    PortalModule,
    OverlayModule,
    TemplateGeneratorModalComponent,
  ],
  templateUrl: './template-generator.component.html',
  styleUrl: './template-generator.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class TemplateGeneratorComponent implements OnInit, OnDestroy {
  @ViewChild('static-template-select') staticTemplateSelect:
    | ElementRef
    | undefined;
  @ViewChild('dynamic-template-select') dynamicTemplateSelect:
    | ElementRef
    | undefined;

  // Gridster options
  gridOptions: GridsterConfig = {};
  gridsterDynamic: { [mode: number]: Gridster | undefined } = {
    1: undefined,
    2: undefined,
    3: undefined,
  };
  gridsterStatic: { [mode: number]: Gridster | undefined } = {
    1: undefined,
    2: undefined,
    3: undefined,
  };

  //staticTemplate: GridsterItem[] = [];
  //dynamicTemplate: GridsterItem[] = [];

  //gridsterItems: CustomComponent[] = [];

  availableComponents = this.componentRegistry.getComponentRegistry();

  savedSlugs: CustomSlug[] = [];
  selectedSlug?: CustomSlug;
  selectedSlugName?: string;
  currentSlug: CustomSlug | null = null;

  savedCustomTemplates: CustomTemplate[] = [];
  selectedCustomTemplatesStaticUUID: string | null = null; //Static template generated with static template

  selectedCustomTemplatesDynamicUUID: { [mode: number]: string | null } = {
    1: null,
    2: null,
    3: null,
  }; //Dynamic template generated with dynamic template
  selectedCustomTemplatesDynamicStaticUUID: { [mode: number]: string | null } =
    { 1: null, 2: null, 3: null }; //Dynamic template generated with static template

  // For modal editing
  isModalOpen: boolean = false;
  editedComponent: CustomComponent | undefined;
  editedComponentTemplateUUID: string | null = null;
  //template: any;

  @ViewChildren(TemplateGeneratorDynamicComponent)
  template_generator_dynamicComponent!: QueryList<TemplateGeneratorDynamicComponent>;
  @ViewChildren(TemplateGeneratorStaticComponent)
  template_generator_staticComponent!: QueryList<TemplateGeneratorStaticComponent>;

  private destroy$ = new Subject<void>();
  private resizeSubject = new Subject<void>();
  public dataLoaded = false;
  private overlayRef: OverlayRef | null = null;

  constructor(
    public componentRegistry: ComponentRegistryService,
    private templateService: TemplateService,
    private changeDetectorRef: ChangeDetectorRef,
    public overlay: Overlay,
    public router: Router
  ) {}

  // Lifecycle hooks
  ngOnInit(): void {

    this.gridOptions = {
      gridType: GridType.VerticalFixed,
      compactType: CompactType.None,
      margin: 0,
      outerMargin: false,
      draggable: {
        enabled: true,
        ignoreContent: false,
        dropOverItems: false,
        dragHandleClass: 'grid-item',
      },
      resizable: {
        enabled: true,
      },
      pushItems: false,
      pushResizeItems: false,
      swap: false,
      minCols: 12,
      maxCols: 12,
      minItemRows: 1,
      maxItemRows: 50,
      minItemCols: 1,
      maxItemCols: 12,
      minRows: 30,
      maxRows: Infinity,
      rowHeight: 50,
      fixedRowHeight: 50,
      setGridSize: true,
      useTransformPositioning: true,
      mobileBreakpoint: 10,
      displayGrid: DisplayGrid.Always,
      enableEmptyCellClick: false,
      emptyCellDrag: false,
      keepFixedHeightInMobile: true,
      keepFixedWidthInMobile: false,
      enableOccupiedCellDrop: true,
      dragEnabled: true,
      dragAndDrop: true,
      enableEmptyCellDrop: true,
    };

    this.initializeData();

    this.resizeSubject
      .pipe(
        debounceTime(200),
        filter(() => this.dataLoaded)
      )
      .subscribe(() => {
        this.updateGridsterLayouts();
      });
  }

  onDragStop(event: any) {
  }

  onDropToChild(event: any) {
  }

  ngAfterViewInit() {
    // The components are now available
  }

  onTabChange(event: any) {
    var newMode = 1 as 1 | 2 | 3;
    if (event && event.index) {
      newMode = (event.index + 1) as 1 | 2 | 3;
    }
    //const { width, height } =  this.templateService.getViewportSize(newMode);

    // Update the TemplateService with the new viewport size
    //this.templateService.setCustomViewportSize(width, height);

    // Wait for the DOM to update
    setTimeout(() => {
      // Trigger a resize event to make gridster recalculate its dimensions
      window.dispatchEvent(new Event('resize'));

      for (const mode in this.gridsterDynamic) {
        if (this.gridsterDynamic[mode]) {
          this.gridsterDynamic[mode]?.updateGrid();
        }
      }
      for (const mode in this.gridsterStatic) {
        if (this.gridsterStatic[mode]) {
          this.gridsterStatic[mode]?.updateGrid();
        }
      }
    }, 100);
  }

  onGridsterDynamicComponentReady(mode: 1 | 2 | 3, event: any) {
    this.gridsterDynamic[mode] = event.gridster;
  }

  onGridsterStaticComponentReady(mode: 1 | 2 | 3, event: any) {
    this.gridsterStatic[mode] = event.gridster;
  }

  // Get methods
  getTemplateGeneratorDynamicComponent(): TemplateGeneratorDynamicComponent[] {
    return this.template_generator_dynamicComponent.toArray();
  }

  getTemplateGeneratorStaticComponent(): TemplateGeneratorStaticComponent[] {
    return this.template_generator_staticComponent.toArray();
  }

  getCurrentTemplatesArrays(): [
    { [mode: number]: string | null },
    { [mode: number]: string | null }
  ] {
    /*const dynamicTemplates = this.getTemplateGeneratorDynamicComponent();
    const dynamicTemplateUUIDs: string[] = [];
    const staticTemplateUUIDs: string[] = [];
    dynamicTemplates.forEach(template => {
      dynamicTemplateUUIDs =  ?? '';
      staticTemplateUUIDs[template.mode] = template.selectedCustomTemplatesDynamicStaticUUID ?? '';
    });*/

    if (
      this.selectedCustomTemplatesDynamicUUID &&
      this.selectedCustomTemplatesDynamicStaticUUID
    ) {
      return [
        this.selectedCustomTemplatesDynamicUUID,
        this.selectedCustomTemplatesDynamicStaticUUID,
      ];
    }
    return [[], []];
  }

  getSelectedTemplate(uuid: string | null): CustomTemplate | null {
    //Check if uuid is an actual uuid, and if it's in savedCustomTemplates
    if (uuid && this.isValidUUID(uuid)) {
      const template = this.savedCustomTemplates.find(
        (template) => template.uuid === uuid
      );
      if (template) {
        return template;
      } else {
        console.error('Saved template not found:', uuid);
      }
    } else {
      //console.error("Template not a valid UUID:", uuid);
    }
    return null;
  }

  setSelectedTemplate(uuid: string | null): CustomTemplate | null {
    if (uuid) {
      const template = this.savedCustomTemplates.find(
        (template) => template.uuid === uuid
      );
      if (template) {
        return template;
      }
    }
    return null;
  }

  setDynamicStaticUUID(mode: 1 | 2 | 3, uuid: string | null) {
    this.selectedCustomTemplatesDynamicStaticUUID[mode] = uuid;
  }

  setDynamicDynamicUUID(mode: 1 | 2 | 3, uuid: string | null) {
    this.selectedCustomTemplatesDynamicUUID[mode] = uuid;
  }

  getSelectedTemplateComponents(
    uuid: string | null,
    mode: 1 | 2 | 3
  ): CustomComponent[] | [] {
    const template = this.getSelectedTemplate(uuid);
    if (template && 'customComponents' in template) {
      const componentsWithMode = template.customComponents.filter(
        (component) => component.mode == mode
      );
      return componentsWithMode;
    }
    return [];
  }

  // Load methods
  loadTemplates() {
    this.templateService.getCustomTemplates().subscribe((templates) => {
      templates.forEach((template) => {
        template.customComponents.forEach((component) => {
          if (typeof component.getComponent === 'function') {
            component.getComponent(this.componentRegistry);
          } else {
            console.warn(
              'getComponent method not found on component:',
              component
            );
          }
        });
      });
      this.savedCustomTemplates = templates;
    });
  }

  loadSlugs() {
    this.templateService.getSlugs().subscribe((slugs) => {
      this.savedSlugs = slugs;
    });
  }

  loadAllTemplatesFromSaved(isstatic: boolean): CustomTemplate[] {
    const templates = this.savedCustomTemplates
      .filter((template) => template.isstatic == isstatic)
      .sort((a, b) => a.name.localeCompare(b.name));

    if (templates) {
      return templates;
    } else {
      console.error('Templates not found');
      return [];
    }
  }

  loadSelectedSlug() {
    const slug = this.savedSlugs.find(
      (slug) => slug.slug === this.selectedSlugName
    );
    if (slug) {
      //Loop through this.getSelectedTemplateComponents and set corresponding uuid from slug
      //const dynamicTemplates = this.getTemplateGeneratorDynamicComponent();
      //const staticTemplates = this.getTemplateGeneratorStaticComponent();

      /*dynamicTemplates.forEach((template, index) => {
        template.selectedCustomTemplatesDynamicUUID = slug.getDynamicTemplateFromMode(template.mode) ?? null;
        template.selectedCustomTemplatesDynamicStaticUUID = slug.getStaticTemplateFromMode(template.mode) ?? null;
      });

      this.selectedCustomTemplatesStaticUUID = slug.static_template_uuid;*/
      this.selectedCustomTemplatesDynamicUUID = slug.dynamic_template_uuid;
      this.selectedCustomTemplatesDynamicStaticUUID = slug.static_template_uuid;
    }
  }

  // Save methods
  saveTemplate(customTemplateUUID: { [mode: number]: string | null }) {

    if (customTemplateUUID != null) {
      Object.entries(customTemplateUUID).forEach(([mode, uuid]) => {
        if (uuid) {
          const customTemplate = this.savedCustomTemplates.find(
            (template) => template.uuid === uuid
          );

          if (customTemplate) {
            // Log the initial state
            customTemplate.customComponents.forEach((comp) => {
              if (comp.name === 'ComponentContainerComponent') {
                if (comp.childComponents?.length) {
                  const isResponsive = comp.inputs['isResponsive']?.value;
                  if (isResponsive) {
                    // For responsive containers, sort by responsivePosition
                    comp.childComponents.sort((a, b) => {
                      const posA =
                        a.settings?.['responsivePosition']?.value ?? 0;
                      const posB =
                        b.settings?.['responsivePosition']?.value ?? 0;
                      return posA - posB;
                    });
                  } else {
                    // For non-responsive containers, update y positions
                    comp.childComponents.forEach((child, index) => {
                      const oldPosition = child.y;
                      child.y = index;
                    });
                  }
                }
              }
            });

            const dto = CustomTemplate.convertToDTO(customTemplate);

            this.templateService.postTemplate(customTemplate).subscribe(
              (savedTemplate) => {
              },
              (error) => {
                console.error('\nError saving template:', error);
              }
            );
          } else {
            console.error('Template not found while saving template:', uuid);
          }
        }
      });
    }
  }

  saveTemplateAsNew(isstatic: boolean) {
    const newTemplateName = prompt('Enter a name for the new template');
    if (newTemplateName) {
      const newTemplate = new CustomTemplate(newTemplateName, isstatic, [], 1);

      if (isstatic) {
        //this.selectedCustomTemplatesStaticUUID = newTemplate.uuid;
      } else {
        //this.selectedCustomTemplatesDynamicUUID = newTemplate.uuid;
      }
      this.savedCustomTemplates.push(newTemplate);
      this.saveTemplate([newTemplate.uuid]);
    }
  }

  saveSlug() {
    if (this.currentSlug) {
      const slugDTO = CustomSlug.convertToDTO(this.currentSlug);
      this.templateService.saveCustomSlug(slugDTO).subscribe(
        (response) => {
          // Update the savedSlugs array with the updated slug
          const index = this.savedSlugs.findIndex(
            (s) => s.uuid === this.currentSlug?.uuid
          );
          if (index !== -1 && this.currentSlug) {
            this.savedSlugs[index] = this.currentSlug;
          }
        },
        (error) => {
          console.error('Error saving slug:', error);
        }
      );
    }
  }

  saveSlugAsNew() {
    const newSlugName = prompt('Enter a name for the new slug');
    if (newSlugName) {
      const [dynamicTemplateUUIDs, staticTemplateUUIDs] =
        this.getCurrentTemplatesArrays();
      this.savedSlugs.push(
        new CustomSlug(
          newSlugName,
          staticTemplateUUIDs ?? [],
          dynamicTemplateUUIDs ?? []
        )
      );
      this.selectedSlugName = newSlugName;
      this.saveSlug();
    }
  }

  // Add/Remove/Edit methods
  addComponentToTemplate(
    componentName: string | null,
    template: CustomTemplate | null,
    mode: 1 | 2 | 3
  ) {
    if (componentName && template) {
      var component = this.componentRegistry.getComponentByName(componentName);
      if (component) {
        var componentDTO = CustomComponent.convertToDTO(component);
        var newComponent = CustomComponent.fromDTO(componentDTO);
        if (newComponent) {
          newComponent.generateUUID();
          newComponent.component = component.component;
          newComponent.mode = mode;
          template.customComponents.push(newComponent);
          return newComponent;
        } else {
          console.error(`Component ${componentName} not found in registry!`);
        }
      } else {
        console.error('Selected template not found, or none selected.');
      }
    } else {
      console.error(
        'Component name or template not found:',
        componentName,
        template
      );
    }
    return null;
  }

  addComponentToModal(
    componentName: string | null,
    modal: CustomModal | null,
    mode: 1 | 2 | 3
  ) {
    if (componentName && modal) {
      var component = this.componentRegistry.getComponentByName(componentName);
      if (component) {
        var componentDTO = CustomComponent.convertToDTO(component);
        var newComponent = CustomComponent.fromDTO(componentDTO);
        if (newComponent) {
          newComponent.generateUUID();
          newComponent.component = component.component;
          newComponent.mode = mode;
          modal.customComponents.push(newComponent);
          return newComponent;
        } else {
          console.error(`Component ${componentName} not found in registry!`);
        }
      } else {
        console.error('Selected modal not found, or none selected.');
      }
    } else {
      console.error('Component name or modal not found:', componentName, modal);
    }
    return null;
  }

  removeComponentFromGridster(item: CustomComponent, uuid: string | null) {
    if (confirm('Er du sikker på at du vil fjerne denne component?')) {
      const index =
        this.getSelectedTemplate(uuid)?.customComponents.indexOf(item);
      if (index !== undefined && index !== -1) {
        this.getSelectedTemplate(uuid)?.customComponents.splice(index, 1);
        this.onTabChange(null);
      }
    }
  }

  copyComponentInTemplate(
    component: CustomComponent,
    templateUUID: string | null
  ) {
    const template = this.getSelectedTemplate(templateUUID);
    if (!template) {
      console.error('No template found for copy operation');
      return;
    }

    const copy = this.cloneComponentTree(
      component,
      component.mode as 1 | 2 | 3,
      component.parentUUID
    );
    copy.x = component.x ?? 0;
    copy.y = (component.y ?? 0) + (component.rows ?? 1);

    const sourceIndex = template.customComponents.findIndex(
      (comp) => comp.uuid === component.uuid
    );
    if (sourceIndex === -1) {
      template.customComponents.push(copy);
    } else {
      template.customComponents.splice(sourceIndex + 1, 0, copy);
    }

    this.changeDetectorRef.detectChanges();
    this.updateGridsterLayouts();
  }

  private cloneComponentTree(
    component: CustomComponent,
    mode: 1 | 2 | 3,
    parentUUID?: string
  ): CustomComponent {
    const clonedSettings = component.settings
      ? JSON.parse(JSON.stringify(component.settings))
      : undefined;
    if (clonedSettings && 'customID' in clonedSettings) {
      clonedSettings['customID'] = { type: 'string', value: '' };
    }

    const clonedInputs = component.inputs
      ? JSON.parse(JSON.stringify(component.inputs))
      : undefined;
    const copy = new CustomComponent(
      component.name,
      component.x,
      component.y,
      component.cols,
      component.rows,
      component.customCSS,
      clonedSettings,
      clonedInputs,
      mode
    );

    copy.parentUUID = parentUUID;

    if (component.childComponents?.length) {
      copy.childComponents = component.childComponents.map((child) =>
        this.cloneComponentTree(child, mode, copy.uuid)
      );
    }

    return copy;
  }

  editComponentFromGridster(uuid: string, templateUUID: string | null) {
    const template = this.getSelectedTemplate(templateUUID);
    if (!template) return;

    const foundComponent = template.customComponents.find(
      (comp) => comp.uuid === uuid
    );
    if (!foundComponent) return;

    // Create a reference while preserving the prototype chain
    this.editedComponent = Object.create(
      Object.getPrototypeOf(foundComponent),
      Object.getOwnPropertyDescriptors(foundComponent)
    );

    // Ensure component class is set
    if (this.editedComponent && !this.editedComponent.component) {
      const registeredComponent = this.componentRegistry.getComponentByName(
        this.editedComponent.name
      );
      if (registeredComponent?.component) {
        this.editedComponent.component = registeredComponent.component;
      }
    }

    this.editedComponentTemplateUUID = templateUUID;

    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically();

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-dark-backdrop',
        panelClass: 'edit-modal-overlay',
      });

      const portal = new ComponentPortal(EditComponentModalComponent);
      const componentRef = this.overlayRef.attach(portal);

      componentRef.instance.isOpen = true;
      componentRef.instance.component = this.editedComponent;
      componentRef.instance.saveChanges.subscribe(() => this.saveModal());
      componentRef.instance.closeModal.subscribe(() => this.closeModal());

      this.overlayRef.backdropClick().subscribe(() => {
        this.closeModal();
      });
    }
  }

  // Modal methods
  saveModal() {
    if (this.editedComponent) {
      let selectedTemplate = this.getSelectedTemplate(
        this.editedComponentTemplateUUID
      );

      if (selectedTemplate) {
        const index = selectedTemplate.customComponents.findIndex(
          (comp) => comp.uuid === this.editedComponent!.uuid
        );
        if (index !== -1 && this.editedComponent.component) {
          // Update the existing component in place while preserving the prototype chain
          selectedTemplate.customComponents[index] = Object.create(
            Object.getPrototypeOf(this.editedComponent),
            Object.getOwnPropertyDescriptors(this.editedComponent)
          );

          // Force change detection
          this.changeDetectorRef.markForCheck();
          this.changeDetectorRef.detectChanges();

          // Get the specific gridster
          const mode = this.editedComponent.mode;
          const gridster = selectedTemplate.isstatic
            ? this.gridsterStatic[mode]
            : this.gridsterDynamic[mode];

          if (gridster) {
            gridster.updateGrid();
            gridster.api.calculateLayout();
          }
        }
      }
      this.closeModal();
    }
  }

  closeModal() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isModalOpen = false;
  }

  // Commented out methods can be added back if needed
  /* previewLayout() { ... } */
  /* updateSettings() { ... } */
  /* saveTemplates() { ... } */

  isValidUUID(uuid: string): boolean {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  getComponentInputs(component: CustomComponent): {
    [key: string]: { type: string; value: any };
  } {
    /*let inputs: { [key: string]: { type: string, value: any } } = {};

    // Check for inputs in the component's settings
    if (component.inputs) {
      inputs = { ...inputs, ...component.inputs };
    }

    if (component instanceof CustomComponent) {
    }else{
    }

    // Check for inputs in the standalone component
    if (component.component && 'inputs' in component.component) {
      const componentInputs = component.component.inputs as Record<string, unknown>;
      for (const key in componentInputs) {
        if (componentInputs.hasOwnProperty(key)) {
          // If the input is not already in the settings, add it with a default value
          if (!inputs[key]) {
            inputs[key] = { type: 'text', value: '' };
          }
        }
      }
    }*/

    return component.inputs;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onResize() {
    if (this.dataLoaded) {
      this.resizeSubject.next();
    }
  }

  private updateGridsterLayouts() {

    const updateGridster = (gridster: Gridster | undefined) => {
      if (gridster) {
        gridster.updateGrid();
        gridster.api.calculateLayout();
        this.changeDetectorRef.detectChanges();
      }
    };

    for (const mode in this.gridsterDynamic) {
      updateGridster(this.gridsterDynamic[mode]);
    }

    for (const mode in this.gridsterStatic) {
      updateGridster(this.gridsterStatic[mode]);
    }
  }

  copyComponentsFromMode(fromMode: 1 | 2 | 3, toMode: 1 | 2 | 3) {

    // Get source components
    const sourceComponents = this.getSelectedTemplateComponents(
      this.selectedCustomTemplatesDynamicUUID[fromMode],
      fromMode
    );

    if (!sourceComponents.length) {
      console.warn('No components found in source mode');
      return;
    }

    // Deep copy function for CustomComponent
    const deepCopyComponent = (component: CustomComponent): CustomComponent => {
      // Create new component instance
      const newComponent = new CustomComponent(
        component.name,
        component.x,
        component.y,
        component.cols,
        component.rows,
        component.customCSS,
        { ...component.settings },
        { ...component.inputs },
        toMode, // Set the new mode
        undefined, // Generate new UUID
        component.childComponents || [] // Initialize empty child components array
      );

      // Recursively copy child components if they exist
      if (component.childComponents && component.childComponents.length > 0) {
        newComponent.childComponents = component.childComponents.map((child) =>
          deepCopyComponent(child)
        );
      }

      return newComponent;
    };

    // Get the target template
    const targetTemplate = this.getSelectedTemplate(
      this.selectedCustomTemplatesDynamicUUID[toMode]
    );

    if (!targetTemplate) {
      console.error('Target template not found');
      return;
    }

    // Create deep copies of all components with the new mode
    const copiedComponents = sourceComponents.map((component) =>
      deepCopyComponent(component)
    );

    // Remove existing components in the target mode
    targetTemplate.customComponents = targetTemplate.customComponents.filter(
      (comp) => comp.mode !== toMode
    );

    // Add the copied components
    targetTemplate.customComponents.push(...copiedComponents);

    // Force change detection
    this.changeDetectorRef.detectChanges();
  }

  /* updateGridsterItem(item: CustomComponent) {
    const gridster = item.mode === 1 ? this.gridsterDynamic[1] :
                     item.mode === 2 ? this.gridsterDynamic[2] :
                     item.mode === 3 ? this.gridsterDynamic[3] :
                     this.gridsterStatic[item.mode];

    if (gridster) {
      gridster.updateItem(item);
      setTimeout(() => {
        gridster.resize();
        gridster.reflow();
      }, 0);
    }
  } */

  private async initializeData() {
    try {
      // Load templates first
      await new Promise<void>((resolve) => {
        this.templateService.getCustomTemplates().subscribe((templates) => {
          this.savedCustomTemplates = templates;
          resolve();
        });
      });

      // Then load slugs
      await new Promise<void>((resolve) => {
        this.templateService.getSlugs().subscribe((slugs) => {
          this.savedSlugs = slugs;
          resolve();
        });
      });

      this.dataLoaded = true;
      this.updateGridsterLayouts();
    } catch (error) {
      console.error('[TemplateGenerator] Error loading initial data:', error);
    }
  }

  updateComponentChildren(
    containerUUID: string,
    childComponents: CustomComponent[],
    mode: number
  ) {
    const template = this.getSelectedTemplate(
      this.selectedCustomTemplatesDynamicUUID[mode]
    );
    if (template) {
      const containerComponent = template.customComponents.find(
        (comp) => comp.uuid === containerUUID
      );
      if (containerComponent) {
        containerComponent.childComponents = childComponents;
      }
    }
  }

  moveComponentToContainer(
    sourceUUID: string,
    containerUUID: string,
    mode: number,
    x: number,
    y: number
  ) {

    const template = this.getSelectedTemplate(
      this.selectedCustomTemplatesDynamicUUID[mode]
    );
    if (!template) {
      console.error('Template not found');
      return;
    }

    // Helper to find a component and its current parent UUID without removing it
    const findComponentAndParentRecursively = (
      components: CustomComponent[],
      parentUUID?: string
    ): { component?: CustomComponent; currentParentUUID?: string } => {
      for (const comp of components) {
        if (comp.uuid === sourceUUID) {
          return { component: comp, currentParentUUID: parentUUID };
        }
        if (comp.childComponents?.length) {
          const result = findComponentAndParentRecursively(
            comp.childComponents,
            comp.uuid
          );
          if (result.component) return result;
        }
      }
      return { component: undefined };
    };

    // If moving within the same container, just update position and return
    const sameContainerCheck = findComponentAndParentRecursively(
      template.customComponents,
      this.selectedCustomTemplatesDynamicUUID[mode]!
    );
    if (
      sameContainerCheck.component &&
      sameContainerCheck.currentParentUUID === containerUUID
    ) {
      sameContainerCheck.component.x = x;
      sameContainerCheck.component.y = y;
      this.changeDetectorRef.detectChanges();
      return;
    }

    // Helper function to find and remove component recursively
    const findAndRemoveComponentRecursively = (
      components: CustomComponent[]
    ): { component?: CustomComponent; currentParentUUID?: string } => {
      for (let i = 0; i < components.length; i++) {
        const comp = components[i];
        if (comp.uuid === sourceUUID) {
          // Only prevent moves to current container if not moving to main gridster
          const currentContainer = comp.parentUUID;
          // Allow repositioning within same container by not blocking here
          const removed = components.splice(i, 1)[0];
          return { component: removed, currentParentUUID: currentContainer };
        }
        if (comp.childComponents?.length) {
          const result = findAndRemoveComponentRecursively(
            comp.childComponents
          );
          if (result.component) return result;
        }
      }
      return { component: undefined };
    };

    // Find and remove the component from its current location
    const { component: componentToMove, currentParentUUID } =
      findAndRemoveComponentRecursively(template.customComponents);

    if (!componentToMove) {
      return;
    }

    // Update component position and parent
    componentToMove.x = x;
    componentToMove.y = y;
    componentToMove.parentUUID = containerUUID;

    // If containerUUID is the template's UUID, add to main components
    if (containerUUID === this.selectedCustomTemplatesDynamicUUID[mode]) {
      template.customComponents.push(componentToMove);
    } else {
      // Find the container component recursively
      const findContainerRecursively = (
        components: CustomComponent[]
      ): CustomComponent | undefined => {
        for (const comp of components) {
          if (comp.uuid === containerUUID) {
            return comp;
          }
          if (comp.childComponents?.length) {
            const found = findContainerRecursively(comp.childComponents);
            if (found) return found;
          }
        }
        return undefined;
      };

      const containerComponent = findContainerRecursively(
        template.customComponents
      );
      if (!containerComponent) {
        console.error('Container component not found:', containerUUID);
        return;
      }

      // Initialize childComponents array if it doesn't exist
      if (!containerComponent.childComponents) {
        containerComponent.childComponents = [];
      }

      // Add to container's children
      containerComponent.childComponents.push(componentToMove);
    }

    // Force change detection
    this.changeDetectorRef.detectChanges();
  }

  updateSlugDevelopment(event: MatCheckboxChange) {
    if (this.currentSlug) {
      this.currentSlug.isDevelopment = event.checked;
      this.saveSlug();
    }
  }

  onSlugChange(event: any) {
    const selectedSlugName = event.target.value;
    if (selectedSlugName) {
      this.currentSlug =
        this.savedSlugs.find((slug) => slug.slug === selectedSlugName) || null;
      if (this.currentSlug) {
        // Set the template UUIDs based on the selected slug
        this.selectedCustomTemplatesDynamicStaticUUID =
          this.currentSlug.static_template_uuid;
        this.selectedCustomTemplatesDynamicUUID =
          this.currentSlug.dynamic_template_uuid;
      }
    }
  }

  createNewSlug() {
    const newSlugName = prompt('Enter a name for the new slug:');
    if (newSlugName) {
      const newSlug = new CustomSlug(newSlugName);
      this.currentSlug = newSlug;
      this.selectedSlugName = newSlugName;
      this.savedSlugs.push(newSlug);
      this.saveSlug();
    }
  }
}
